import envConfig from "@/config/envConfig";
import { getAuthHeaders } from "@/utils/authToken";

type updateUserPayloadType = {
  fullName: string;
  email: string;
  phoneNumber?: string;
  country?: string;
  state?: string;
  city?: string;
  avatar?: any;
};

export const updateUser = async ({
  fullName,
  email,
  phoneNumber,
  country,
  state,
  city,
  avatar,
}: updateUserPayloadType) => {
  try {
    if (avatar === "delete") {
      await fetch(`${envConfig.publicServerUrl}/api/v1/users/remove-profile-picture`, {
        method: "POST",
        headers: getAuthHeaders(),
        credentials: "include",
      });
    }

    const formData = new FormData();
    formData.append("fullName", fullName);
    formData.append("email", email);
    formData.append("phoneNumber", phoneNumber || "");
    formData.append("country", country || "");
    formData.append("state", state || "");
    formData.append("city", city || "");
    // Only append image when it's a valid File (not null, not "delete")
    if (avatar && avatar instanceof File) {
      formData.append("image", avatar);
    }

    const response = await fetch(`${envConfig.publicServerUrl}/api/v1/users/update-profile`, {
      method: "PATCH",
      headers: getAuthHeaders(),
      body: formData,
      credentials: "include",
    });

    if (!response.ok) {
      const errRespData = await response.json().catch(() => ({}));
      const errMessage =
        errRespData?.errors?.[0]?.message ?? errRespData?.message ?? "Failed to update user!";
      const err = new Error(errMessage) as Error & { code?: string };
      err.code = errRespData?.code;
      throw err;
    }

    const respData = await response.json();
    return respData;
  } catch (err) {
    throw err;
  }
};

export const deleteUser = async () => {
  try {
    const response = await fetch(`${envConfig.publicServerUrl}/api/v1/users/deactivate`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      credentials: "include",
    });

    const respData = await response.json();
    if (!response.ok) {
      const errMessage =
        respData?.errors?.[0]?.message ?? respData?.message ?? "Something went wrong!";
      throw new Error(errMessage);
    }

    return respData;
  } catch (err) {
    throw err;
  }
};

export const userLogout = async () => {
  try {
    const response = await fetch(`${envConfig.publicServerUrl}/api/auth/reader-logout`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error(`Logout failed: ${response.statusText}`);
    }

    const respData = await response.json();
    return respData;
  } catch (err) {
    throw err;
  }
};

export const subscribeUser = async (email: string) => {
  try {
    const response = await fetch(`${envConfig.publicServerUrl}/api/subscribers`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      throw new Error("Failed to subscribe");
    }

    return await response.json();
  } catch (error) {
    console.error(error);
    throw error;
  }
};
